import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

import { onMounted, ref, nextTick } from 'vue';
import ServiceOutageModal from './ServiceOutageModal.vue';
import { UserModalsApi } from '@/api/UserModalsApi';

// После того как модалку закрыли — не дёргаем эндпоинт повторно
// в рамках этой сессии вкладки. Ключи свои, чужие не переиспользуем.
const SESSION_SKIP_KEY = 'serviceOutage.skip';

// Ответ shouldShow=false кэшируем ненадолго, чтобы не делать запрос на каждой загрузке страницы.
const SESSION_NOTHING_KEY = 'serviceOutage.checkedAt';
const NOTHING_TTL_MS = 10 * 60 * 1000;

// sessionStorage может бросить (запрещённое хранилище, квота) — кэш здесь лишь оптимизация,
// поэтому ошибки хранилища не должны прерывать проверку и dismiss.

export default /*#__PURE__*/_defineComponent({
  __name: 'ServiceOutageModals',
  emits: ["checked"],
  setup(__props, { emit: __emit }) {

// Самодостаточный хост модалки о сбое в работе платформы (FINCH-2630): сам запрашивает
// getServiceOutageModalInfo при входе и показывает модалку.
// Модалка о сбое важнее остальных модалок при входе, поэтому хост сообщает родителю
// результат проверки событием checked: пока проверка не завершилась или модалка открыта и не закрыта,
// TariffNotificationModals.vue остальные модалки не запрашивает. Очерёдность действует в пределах
// одной страницы: во вложенном фрейме хост сразу шлёт checked(false) (см. isNestedFrame).
// checked шлётся при любом исходе, включая ошибки, — иначе хост заблокировал бы все модалки входа.
function readSession(key: string): string | null {
    try {
        return sessionStorage.getItem(key);
    } catch (e) {
        console.error('[ServiceOutageModals] sessionStorage read failed', e);
        return null;
    }
}

function writeSession(key: string, value: string) {
    try {
        sessionStorage.setItem(key, value);
    } catch (e) {
        console.error('[ServiceOutageModals] sessionStorage write failed', e);
    }
}

// Во вложенном фрейме (доска внутри звонка) не показываем: модалка всплыла бы поверх доски
// посреди занятия, а хост на странице звонка и хост в iframe доски стартуют параллельно
// (общий sessionStorage ещё пуст) и показали бы две модалки сразу. Покажет хост верхней страницы.
function isNestedFrame(): boolean {
    return window.self !== window.top;
}

const emit = __emit;

const modalRef = ref<InstanceType<typeof ServiceOutageModal> | null>(null);
const shouldShow = ref(false);
let dismissed = false;

async function onDismiss() {
    if (!shouldShow.value || dismissed) {
        return;
    }
    dismissed = true;
    writeSession(SESSION_SKIP_KEY, '1');
    // post() не бросает: при отказе возвращает isSuccess=false. Показ тогда не запишется
    // на сервере, и в новой вкладке модалка придёт снова — логируем, чтобы это было видно.
    const result = await new UserModalsApi().dismissServiceOutageModal();
    if (!result?.isSuccess) {
        console.error('[ServiceOutageModals] dismiss failed', result);
    }
}

async function resolveShouldShow(): Promise<boolean> {
    if (isNestedFrame() || readSession(SESSION_SKIP_KEY)) {
        return false;
    }
    const checkedAt = Number(readSession(SESSION_NOTHING_KEY));
    if (checkedAt && Date.now() - checkedAt < NOTHING_TTL_MS) {
        return false;
    }
    // get() не бросает и без редиректа на /Error: при сбое возвращает isSuccess=false.
    const info = await new UserModalsApi().getServiceOutageModalInfo();
    if (info?.entity?.shouldShow) {
        return true;
    }
    // Кэшируем «нечего показывать» только для настоящего ответа бэка,
    // чтобы сбой запроса не прятал модалку на 10 минут.
    if (info?.isSuccess) {
        writeSession(SESSION_NOTHING_KEY, String(Date.now()));
    }
    return false;
}

onMounted(async () => {
    let shown = false;
    try {
        if (await resolveShouldShow()) {
            shouldShow.value = true;
            await nextTick();
            if (modalRef.value) {
                modalRef.value.show();
                shown = true;
            } else {
                console.error('[ServiceOutageModals] modal ref is empty, modal not shown');
            }
        }
    } catch (e) {
        console.error('[ServiceOutageModals] check failed', e);
    } finally {
        emit('checked', shown);
    }
});

return (_ctx: any,_cache: any) => {
  return (shouldShow.value)
    ? (_openBlock(), _createBlock(ServiceOutageModal, {
        key: 0,
        ref_key: "modalRef",
        ref: modalRef,
        onHidden: onDismiss
      }, null, 512))
    : _createCommentVNode("", true)
}
}

})